// packages import
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  Autocomplete,
  Box,
  CircularProgress,
  Collapse,
  createFilterOptions,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { FC, useContext, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
// component import
import { Alert } from '../../../common/Alert'
import { CustomController } from '../../../common/CustomController'
import CustomPhoneController from '../../../common/CustomPhoneController'
// others
import { useDebounce } from 'use-debounce'
import { ExternalUserRoleRole, useFetchAllExternalUsersLazyQuery } from '../../../../../generated'
import { GRAPHQL_QUERY_POLICY, NOT_APPLICABLE, NOT_FOUND_EXCEPTION, SOMETHING_WENT_WRONG } from '../../../../constants'
import { CaseContext } from '../../../../context'
import { FormErrors, JudgeListingAutoCompleteProps } from '../../../../interfaceTypes'
import { ExternalUsersResult } from '../../../../interfaceTypes/common'
import { CaseActionType } from '../../caseReducer'

export const JudgeListingAutoComplete: FC<JudgeListingAutoCompleteProps> = ({ currentCourt }) => {
  const [externalUsersData, setExternalUsersData] = useState<ExternalUsersResult>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const { dispatch, isNewJudgeOpen } = useContext(CaseContext)
  const {
    control,
    formState: { errors },
    setValue,
    watch
  } = useFormContext()
  const customError = errors['judgeUser'] as FormErrors['judgeUser']
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const phoneNumberValue = watch('judgeUser.phoneNumber') && (watch('judgeUser.phoneNumber')?.length ?? 0) >= 10

  const handleCollapseNewJudge = () => {
    dispatch({ type: CaseActionType.SET_NEW_jUDGE_OPEN, isNewJudgeOpen: !isNewJudgeOpen })
    setValue('judgeUser.firstName', '')
    setValue('judgeUser.lastName', '')
    setValue('judgeUser.email', '')
    setValue('judgeUser.phoneNumber', '')
    setValue('judgeUser.phoneNumberExtension', '')
  }

  const [fetchAllExternalUsers, { loading: externalUserLoading }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      if (message !== NOT_FOUND_EXCEPTION) {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response } = fetchAllExternalUsers
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            externalUsersData
              ? setExternalUsersData([...externalUsersData, ...externalUsers])
              : setExternalUsersData(externalUsers)
          }
        }
      }
    }
  })

  useEffect(() => {
    fetchAllExternalUsers({
      variables: {
        externalUsersInput: {
          roles: [ExternalUserRoleRole.Judge],

          paginationOptions: {
            limit: 20,
            page: currentPage
          },
          searchValue: debouncedSearchTerm
        }
      }
    })
    // eslint-disable-next-line
  }, [currentPage, debouncedSearchTerm])

  useEffect(() => {
    setCurrentPage(1)
    setExternalUsersData([])
  }, [debouncedSearchTerm])

  return (
    <Box>
      <Collapse in={!isNewJudgeOpen} sx={{ width: '100%' }}>
        <FormControl variant="outlined" fullWidth>
          <Controller
            name="judgeUser"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                fullWidth
                disabled={currentCourt}
                options={externalUsersData || []}
                value={value}
                filterOptions={createFilterOptions({ ignoreCase: true })}
                loading={externalUserLoading}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(event, item) => {
                  onChange(item)
                }}
                onInputChange={(event, newInputValue) => {
                  setSearchTerm(newInputValue)
                }}
                ListboxProps={{
                  onScroll: event => {
                    const listboxNode = event.currentTarget
                    if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                      setCurrentPage(currentPage + 1)
                    }
                  },
                  role: 'list-box',
                  style: {
                    maxHeight: '190px'
                  }
                }}
                getOptionLabel={item => {
                  const { firstName, lastName, displayName, email } = item || {}
                  const name = displayName?.trim() || `${firstName || ''} ${lastName || ''}`.trim()
                  const options = `${name} (${email || NOT_APPLICABLE})`
                  return item.id ? options : ''
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={!!customError?.id?.message}
                    helperText={customError?.id?.message || ''}
                    label="Select Judge"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {externalUserLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            )}
          />
        </FormControl>
      </Collapse>

      <Collapse in={isNewJudgeOpen}>
        <Box boxShadow={2} p={2} mt={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography fontSize="18px">Please add judge details</Typography>

            <IconButton onClick={handleCollapseNewJudge} aria-label="back">
              <ArrowBackIcon color="primary" />
            </IconButton>
          </Box>

          <Grid container spacing={2} rowSpacing={0.3}>
            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="judge.firstName" controllerLabel="First Name" fieldType="text" />
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="judge.lastName" controllerLabel="Last Name" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomPhoneController
                controllerName="judge.phoneNumber"
                controllerLabel="Phone Number"
                fieldType="text"
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="judge.phoneNumberExtension"
                controllerLabel="Extension"
                fieldType="text"
                maxLength={6}
                isDisabled={!phoneNumberValue}
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="judge.email" controllerLabel="Email" fieldType="text" />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  )
}
